import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AdminBooking,
  type CatalogueResponse,
} from "../lib/api.ts";
import {
  addDays,
  formatGbp,
  formatLongDate,
  formatTime,
  todayKey,
} from "../lib/format.ts";
import { AddBookingForm } from "./AddBookingForm.tsx";

interface DiaryProps {
  token: string;
  onUnauthorised: () => void;
}

type Range = "day" | "week";

const STATUS_TONE: Record<string, string> = {
  Confirmed: "ok",
  Completed: "ok",
  PendingPayment: "pending",
  Cancelled: "dead",
  Expired: "dead",
};

export function Diary({ token, onUnauthorised }: DiaryProps) {
  const [anchor, setAnchor] = useState(() => todayKey());
  const [range, setRange] = useState<Range>("day");
  const [bookings, setBookings] = useState<AdminBooking[] | null>(null);
  const [catalogue, setCatalogue] = useState<CatalogueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [actioning, setActioning] = useState<number | null>(null);

  const from = anchor;
  const to = range === "day" ? anchor : addDays(anchor, 6);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      try {
        const result = await api.admin.getBookings(token, from, to, signal);
        setBookings(result.bookings);
      } catch (caught) {
        if ((caught as Error)?.name === "AbortError") return;
        if (caught instanceof ApiError && caught.status === 401) {
          onUnauthorised();
          return;
        }
        setError(
          caught instanceof ApiError
            ? caught.message
            : "The diary could not be loaded.",
        );
      }
    },
    [token, from, to, onUnauthorised],
  );

  useEffect(() => {
    const controller = new AbortController();
    setBookings(null);
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getServices(controller.signal)
      .then(setCatalogue)
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  async function cancel(booking: AdminBooking, forceRefund: boolean) {
    const wording = forceRefund
      ? `Cancel ${booking.customerName}'s appointment and refund ${formatGbp(booking.depositPence)}?`
      : `Cancel ${booking.customerName}'s appointment? The deposit comes back only if this is more than ${catalogue?.cancellationWindowHours ?? 48} hours before it starts.`;
    if (!window.confirm(wording)) return;

    setActioning(booking.id);
    setError(null);
    try {
      const result = await api.admin.cancelBooking(
        token,
        booking.id,
        forceRefund,
      );
      setMessage(result.message);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The cancellation failed.",
      );
    } finally {
      setActioning(null);
    }
  }

  /**
   * Send the confirmation again.
   *
   * The one thing the studio cannot do from anywhere else when a client says
   * nothing arrived - and it carries the consultation link, so it is also how a
   * lost form link is recovered.
   */
  async function resendConfirmation(booking: AdminBooking) {
    if (
      !window.confirm(
        `Send the booking confirmation to ${booking.customerEmail} again?`,
      )
    )
      return;

    setActioning(booking.id);
    setError(null);
    try {
      const result = await api.admin.resendConfirmation(token, booking.id);
      setMessage(`${booking.customerName}: ${result.message}`);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError ? caught.message : "That could not be sent.",
      );
    } finally {
      setActioning(null);
    }
  }

  async function retryRefund(booking: AdminBooking) {
    if (
      !window.confirm(
        `Refund ${formatGbp(booking.depositPence)} to ${booking.customerEmail}?`,
      )
    )
      return;

    setActioning(booking.id);
    setError(null);
    try {
      const result = await api.admin.refundBooking(token, booking.id);
      setMessage(result.message);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError ? caught.message : "The refund failed.",
      );
    } finally {
      setActioning(null);
    }
  }

  const live =
    bookings?.filter((b) =>
      ["Confirmed", "PendingPayment", "Completed"].includes(b.status),
    ) ?? [];
  const dead =
    bookings?.filter((b) => ["Cancelled", "Expired"].includes(b.status)) ?? [];

  return (
    <section className="page stack-lg">
      <div className="diary__toolbar">
        <div className="row">
          <button
            type="button"
            className="btn btn--ghost diary__nav"
            onClick={() =>
              setAnchor(addDays(anchor, range === "day" ? -1 : -7))
            }
            aria-label="Previous"
          >
            &#8592;
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => setAnchor(todayKey())}
          >
            Today
          </button>
          <button
            type="button"
            className="btn btn--ghost diary__nav"
            onClick={() => setAnchor(addDays(anchor, range === "day" ? 1 : 7))}
            aria-label="Next"
          >
            &#8594;
          </button>
        </div>

        <h2 className="diary__title">
          {range === "day"
            ? formatLongDate(`${anchor}T12:00:00Z`)
            : `${formatLongDate(`${from}T12:00:00Z`)} - ${formatLongDate(`${to}T12:00:00Z`)}`}
        </h2>

        <div className="row">
          <div className="segmented" role="group" aria-label="Range">
            {(["day", "week"] as Range[]).map((option) => (
              <button
                key={option}
                type="button"
                className={`segmented__option ${range === option ? "segmented__option--active" : ""}`}
                onClick={() => setRange(option)}
              >
                {option === "day" ? "Day" : "Week"}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => setShowAdd((open) => !open)}
          >
            {showAdd ? "Close" : "Add booking"}
          </button>
        </div>
      </div>

      {showAdd && catalogue && (
        <AddBookingForm
          token={token}
          catalogue={catalogue}
          defaultDate={anchor}
          onCreated={async (reference) => {
            setMessage(`Booking ${reference} added.`);
            setShowAdd(false);
            await load();
          }}
          onUnauthorised={onUnauthorised}
        />
      )}

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      {!bookings && (
        <p className="booking__loading">
          <span className="spinner" /> Loading the diary…
        </p>
      )}

      {bookings && live.length === 0 && (
        <p className="notice">
          Nothing booked {range === "day" ? "on this day" : "this week"}.
        </p>
      )}

      {live.length > 0 && (
        <ul className="diary-list">
          {live.map((booking) => (
            <li
              key={booking.id}
              className={`diary-item diary-item--${STATUS_TONE[booking.status]}`}
            >
              <div className="diary-item__time">
                <strong>{formatTime(booking.startUtc)}</strong>
                <span>{formatTime(booking.endUtc)}</span>
                {range === "week" && (
                  <span className="diary-item__date">
                    {formatLongDate(booking.startUtc).slice(0, -6)}
                  </span>
                )}
              </div>

              <div className="diary-item__body">
                <h3 className="diary-item__name">{booking.customerName}</h3>
                <p className="diary-item__service">{booking.serviceName}</p>
                <p className="diary-item__contact">
                  {booking.customerPhone && (
                    <a href={`tel:${booking.customerPhone}`}>
                      {booking.customerPhone}
                    </a>
                  )}
                  {booking.customerPhone && booking.customerEmail && (
                    <span aria-hidden="true"> · </span>
                  )}
                  <a href={`mailto:${booking.customerEmail}`}>
                    {booking.customerEmail}
                  </a>
                </p>
                {booking.notes && (
                  <p className="diary-item__notes">{booking.notes}</p>
                )}
              </div>

              <div className="diary-item__meta">
                <span className={`pill pill--${STATUS_TONE[booking.status]}`}>
                  {booking.status}
                </span>
                {/* Whether the consultation form is in. Only meaningful once the
                    appointment is actually happening. */}
                {["Confirmed", "Completed"].includes(booking.status) && (
                  <span
                    className={`pill pill--${booking.consultationSubmitted ? "ok" : "pending"}`}
                    title={
                      booking.consultationSubmitted
                        ? "Consultation form received"
                        : "No consultation form yet - chase it from the Consultations tab"
                    }
                  >
                    {booking.consultationSubmitted ? "Form in" : "No form"}
                  </span>
                )}
                <span className="diary-item__money">
                  {booking.paid
                    ? `${formatGbp(booking.depositPence)} paid`
                    : booking.source === "admin"
                      ? "No deposit"
                      : "Unpaid"}
                </span>
                <span className="diary-item__money diary-item__money--due">
                  {formatGbp(booking.pricePence - booking.depositPence)} due
                </span>
                {/* Snapshotted on the booking, so it still reads correctly long
                    after the offer itself has ended or been deleted. */}
                {booking.discountLabel && booking.discountPence && (
                  <span className="diary-item__offer">
                    {booking.discountLabel} &minus;
                    {formatGbp(booking.discountPence)}
                  </span>
                )}
                <span className="diary-item__ref">{booking.reference}</span>

                <button
                  type="button"
                  className="btn btn--danger diary-item__action"
                  disabled={actioning === booking.id}
                  onClick={() => cancel(booking, false)}
                >
                  Cancel
                </button>
                {booking.paid && !booking.refunded && (
                  <button
                    type="button"
                    className="btn btn--quiet diary-item__action"
                    disabled={actioning === booking.id}
                    onClick={() => cancel(booking, true)}
                  >
                    Cancel &amp; refund
                  </button>
                )}
                {/* Only for an appointment that is actually on: the email says
                    "you are booked in". The `.invalid` test matches the API's own
                    rule for an address nothing can be sent to. */}
                {["Confirmed", "Completed"].includes(booking.status) &&
                  !booking.customerEmail.endsWith(".invalid") && (
                    <button
                      type="button"
                      className="btn btn--quiet diary-item__action"
                      disabled={actioning === booking.id}
                      onClick={() => resendConfirmation(booking)}
                    >
                      Resend email
                    </button>
                  )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {dead.length > 0 && (
        <details className="diary__cancelled">
          <summary>{dead.length} cancelled or expired</summary>
          <ul className="diary-list">
            {dead.map((booking) => (
              <li key={booking.id} className="diary-item diary-item--dead">
                <div className="diary-item__time">
                  <strong>{formatTime(booking.startUtc)}</strong>
                </div>
                <div className="diary-item__body">
                  <h3 className="diary-item__name">{booking.customerName}</h3>
                  <p className="diary-item__service">{booking.serviceName}</p>
                  <p className="diary-item__contact">
                    {booking.status}
                    {booking.cancelledBy && ` by ${booking.cancelledBy}`}
                    {booking.refunded &&
                      ` · ${formatGbp(booking.refundPence ?? 0)} refunded`}
                  </p>
                </div>
                <div className="diary-item__meta">
                  <span className="diary-item__ref">{booking.reference}</span>
                  {/* The refund retry path: paid, cancelled, but never refunded. */}
                  {booking.paid &&
                    !booking.refunded &&
                    booking.depositPence > 0 && (
                      <button
                        type="button"
                        className="btn btn--ghost diary-item__action"
                        disabled={actioning === booking.id}
                        onClick={() => retryRefund(booking)}
                      >
                        Refund {formatGbp(booking.depositPence)}
                      </button>
                    )}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
